// CR-6 — degree quiz modal. Opened via Events.OpenQuiz { subject, tier }.
// Opens on a paywall intro: the exam fee (DegreeTiers[tier].cost) is charged
// PER ATTEMPT — win or lose — so failing costs coin and every retake pays the
// fee again. After paying, presents the subject/tier's 5 questions one at a
// time with instant feedback, then a results screen. Passing (≥ QUIZ_PASS
// correct) records the degree tier (no second charge). Lives at the body level.

import { Events, on, emit } from './eventBus.js'
import { Quizzes, QUIZ_PASS } from '../data/quizzes.js'
import { DegreeSubjectLabels, DegreeTiers } from '../config/balance.js'
import { earnDegree, payExamFee, getDegreeTier } from '../game/profileManager.js'
import { getWealth } from '../game/seasonManager.js'
import { formatCoins } from './format.js'
import { BUY_COINS_HTML, wireBuyCoins } from './buyCoinsLink.js'

export function mountQuizModal() {
  // Guard on a selector unique to THIS modal. The study modal shares the
  // `.quiz-modal` skeleton class, so a bare `.quiz-modal` check would match it
  // and skip mounting the real quiz.
  if (document.querySelector('.quiz-modal[data-quiz-root]')) return // idempotent

  const wrap = document.createElement('div')
  wrap.className = 'quiz-modal'
  wrap.setAttribute('data-quiz-root', '')
  wrap.innerHTML = `
    <div class="quiz-modal__backdrop" data-quiz-close></div>
    <div class="quiz-modal__card" role="dialog" aria-modal="true" aria-label="Degree quiz">
      <button class="quiz-modal__x" data-quiz-close aria-label="Close">×</button>
      <div class="quiz-modal__head">
        <div class="quiz-modal__title" data-quiz-title>—</div>
        <div class="quiz-modal__progress" data-quiz-progress></div>
      </div>
      <div class="quiz-modal__body" data-quiz-body></div>
    </div>
  `
  document.body.appendChild(wrap)

  const titleEl = wrap.querySelector('[data-quiz-title]')
  const progressEl = wrap.querySelector('[data-quiz-progress]')
  const bodyEl = wrap.querySelector('[data-quiz-body]')

  let subject = null
  let tier = null
  let questions = []
  let idx = 0
  let score = 0
  let answered = false

  const close = () => wrap.classList.remove('open')
  wrap.querySelectorAll('[data-quiz-close]').forEach((b) => b.addEventListener('click', close))
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && wrap.classList.contains('open')) close()
  })

  const open = ({ subject: subj, tier: tr }) => {
    const bank = Quizzes[subj]?.[tr]
    if (!bank || getDegreeTier(subj) === tr) return // no quiz, or already held
    subject = subj
    tier = tr
    questions = bank
    idx = 0
    score = 0
    answered = false
    titleEl.textContent = `${DegreeSubjectLabels[subj] ?? subj} — ${DegreeTiers[tr]?.name ?? tr}`
    wrap.classList.add('open')
    renderIntro()
  }

  // Paywall: the exam fee is charged here, once per sitting. Nothing starts
  // until the player explicitly pays — and a retake re-enters here, so it pays
  // again. Re-rendered if affordability changes between showing and clicking.
  function renderIntro() {
    const cost = DegreeTiers[tier]?.cost ?? 0
    const subjLabel = DegreeSubjectLabels[subject] ?? subject
    const tierName = DegreeTiers[tier]?.name ?? tier
    const wealth = getWealth()
    const affordable = wealth >= cost
    progressEl.textContent = `${questions.length} questions · pass ${QUIZ_PASS}/${questions.length} to earn the degree`
    bodyEl.innerHTML = `
      <div class="quiz-modal__intro">
        <p class="quiz-modal__intro-lead">You're about to sit the <strong>${escapeHtml(subjLabel)} ${escapeHtml(tierName)}</strong> exam.</p>
        <div class="quiz-modal__fee"><span>Exam fee</span><strong>${formatCoins(cost)}</strong></div>
        <p class="quiz-modal__intro-note">⚠️ The fee is charged <strong>now</strong> and is <strong>non-refundable</strong> — you pay it whether you pass or fail, and <strong>every retake costs the fee again</strong>. Answer ${QUIZ_PASS} of ${questions.length} correctly to earn the degree.</p>
        ${
          affordable
            ? `<button class="quiz-modal__next quiz-modal__begin" data-begin>Pay ${formatCoins(cost)} &amp; begin →</button>`
            : `<div class="quiz-modal__intro-cant">You need <strong>${formatCoins(cost)}</strong> to sit this exam — you have ${formatCoins(wealth)}. Earn more coin and come back.</div>`
        }
        ${BUY_COINS_HTML}
      </div>
    `
    wireBuyCoins(bodyEl, () => wrap.classList.remove('open'))
    const beginBtn = bodyEl.querySelector('[data-begin]')
    if (beginBtn) {
      beginBtn.addEventListener('click', () => {
        if (!payExamFee(subject, tier)) { renderIntro(); return } // afford changed
        idx = 0
        score = 0
        answered = false
        renderQuestion()
      })
    }
  }

  function renderQuestion() {
    answered = false
    const item = questions[idx]
    progressEl.textContent = `Question ${idx + 1} of ${questions.length} · ${score} correct · pass ${QUIZ_PASS}/${questions.length}`
    bodyEl.innerHTML = `
      <div class="quiz-modal__q">${escapeHtml(item.q)}</div>
      <div class="quiz-modal__choices" data-choices>
        ${item.choices.map((c, i) => `
          <button class="quiz-modal__choice" data-choice="${i}">${escapeHtml(c)}</button>
        `).join('')}
      </div>
      <div class="quiz-modal__note" data-note></div>
      <div class="quiz-modal__foot">
        <button class="quiz-modal__next" data-next hidden>${idx + 1 < questions.length ? 'Next question →' : 'See results →'}</button>
      </div>
    `
    const noteEl = bodyEl.querySelector('[data-note]')
    const nextBtn = bodyEl.querySelector('[data-next]')
    bodyEl.querySelectorAll('[data-choice]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (answered) return
        answered = true
        const chosen = Number(btn.dataset.choice)
        const correct = item.answer
        if (chosen === correct) score += 1
        bodyEl.querySelectorAll('[data-choice]').forEach((b) => {
          const i = Number(b.dataset.choice)
          b.disabled = true
          if (i === correct) b.classList.add('correct')
          else if (i === chosen) b.classList.add('wrong')
        })
        if (item.note) {
          noteEl.textContent = (chosen === correct ? '✓ ' : '✗ ') + item.note
          noteEl.classList.add('show', chosen === correct ? 'ok' : 'bad')
        }
        nextBtn.hidden = false
        nextBtn.focus()
      })
    })
    nextBtn.addEventListener('click', () => {
      if (idx + 1 < questions.length) { idx += 1; renderQuestion() }
      else renderResults()
    })
  }

  function renderResults() {
    const passed = score >= QUIZ_PASS
    const cost = DegreeTiers[tier]?.cost ?? 0
    let earned = false
    if (passed) earned = earnDegree(subject, tier) // records tier (fee already paid)
    emit(Events.QuizFinished, { subject, tier, score, passed, earned })

    progressEl.textContent = ''
    const subjLabel = DegreeSubjectLabels[subject] ?? subject
    const tierName = DegreeTiers[tier]?.name ?? tier
    let resultHtml
    if (passed) {
      resultHtml = `
        <div class="quiz-modal__result-icon ok">🎓</div>
        <div class="quiz-modal__result-title">Degree earned!</div>
        <p class="quiz-modal__result-sub">You scored <strong>${score}/${questions.length}</strong> and earned your <strong>${subjLabel} ${tierName}</strong>.${tier === 'PHD' ? ' Your PhD permanently adds <strong>+1</strong> to every paw click!' : ''}</p>
      `
    } else {
      resultHtml = `
        <div class="quiz-modal__result-icon bad">📚</div>
        <div class="quiz-modal__result-title">Not quite</div>
        <p class="quiz-modal__result-sub">You scored <strong>${score}/${questions.length}</strong> — you need <strong>${QUIZ_PASS}</strong> to pass. Your <strong>${formatCoins(cost)}</strong> exam fee was spent; study up and you can pay to retake any time.</p>
      `
    }
    bodyEl.innerHTML = `
      <div class="quiz-modal__result">
        ${resultHtml}
        <button class="quiz-modal__next" data-done>Close</button>
      </div>
    `
    bodyEl.querySelector('[data-done]').addEventListener('click', close)
  }

  on(Events.OpenQuiz, open)
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))
}
